import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import { ArchiveModalV1, ContextMenu, Fieldset, Input, ModalV1, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

interface IProps {
  segment: Segment;
}

function OptionsCell({ segment }: Readonly<IProps>): JSX.Element {
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const [isRenameModalOpen, toggleRenameModalOpen] = useToggle(false);
  const { Goals, Segments } = useStore();
  const { t } = useTranslation();

  async function handleRename() {
    try {
      await segment.save();
      toast(t('Renamed to "{{name}}"', { name: segment.name }));
      toggleRenameModalOpen();

      // If err is instance of Error, it should be of type any.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast(
        t('Something went wrong while saving your changes:\n{{error}}', {
          error: error.message,
        }),
        {
          type: 'error',
        },
      );
    }
  }

  async function handleArchive() {
    const goals = Goals.list({ filters: { segment: segment.id, is_archived__ne: true } });
    await when(() => !goals.isPending);
    if (goals.pagination.count > 0) {
      toast(
        t(
          "Can't archive this conversion pixel because it is used by one or more campaigns or flights.",
        ),
        { type: 'error' },
      );
      return;
    }
    try {
      await segment.patch({ is_archived: true });
      Segments.remove(segment.id);
      toast(t('Archived {{name}}', { name: segment.name }));
      toggleArchiveModalOpen();

      // If err is instance of Error, it should be of type any.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast(
        t('Something went wrong while saving your changes:\n{{error}}', {
          error: error.message,
        }),
        {
          type: 'error',
        },
      );
    }
  }

  function handleNameChange(newValue?: string) {
    segment.set({ name: newValue });
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item onClick={toggleRenameModalOpen} prefix={getIconForAction('rename')}>
          {t('Rename')}
        </ContextMenu.Item>
        <ContextMenu.Item
          onClick={toggleArchiveModalOpen}
          prefix={getIconForAction('archive')}
          theme={'danger'}
        >
          {t('Archive')}
        </ContextMenu.Item>
      </ContextMenu>
      {isRenameModalOpen && (
        <ModalV1
          confirmButtonText={t('Ok')}
          controlled={true}
          onClose={toggleRenameModalOpen}
          onConfirm={handleRename}
          t={t}
          title={t('Rename Conversion Pixel')}
        >
          <Fieldset>
            <Input
              label={t('Name')}
              onChange={handleNameChange}
              type={'text'}
              value={segment.name}
            />
          </Fieldset>
        </ModalV1>
      )}
      {isArchiveModalOpen && (
        <ArchiveModalV1
          model={segment}
          onClose={toggleArchiveModalOpen}
          onConfirm={handleArchive}
          successMessage={t('Successfully archived {{name}}.', { name: segment.name })}
          t={t}
        />
      )}
    </>
  );
}

export default observer(OptionsCell);
